// @owners { team: patients-team }
import { COLORS, type ColorVariant, SPACING } from '@alto/design-library-tokens';
import { Description, Icon, Row, useScreenSize } from '@alto/design-system';
import { useFetchAllNotifications, useGetSupportCases } from '@alto/features';
import React, { useCallback, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { closeCart, openCart } from '~shared/actions/ui/cart';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { getSupportCasesWithUnreadMessages } from '~shared/features/messages/selectors/getMessages';
import { VERIFY } from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getBadgeCounts } from '~shared/selectors/getBadgeCounts';
import { getAddFamilyMemberRelationship } from '~shared/selectors/ui/addFamilyMember';
import { getIsCartShowing } from '~shared/selectors/ui/cart';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import {
  isAccountRoute,
  isAddFamilyMemberRoute,
  isHomeRoute,
  isMedsRoute,
  isNotificationsRoute,
  isOrdersRoute,
  isSupportRoute,
} from './routeHelper';
import { LinkLogo, NavBarWrapper } from '~web/features/app/components/DashboardNavbar';
import { Cart } from '~web/features/checkout/components/Cart';

const getColor = (isActive: boolean) => COLORS.TEXT_COLORS[isActive ? 'SECONDARY' : 'GREY'];

const setIconType = (isActive: boolean): ColorVariant => (isActive ? 'secondary' : 'grey');

const NavItems = styled.ul`
  list-style: none;
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
`;

const selectedLinkCss = css`
  color: ${COLORS.TEXT_COLORS.SECONDARY};
  text-decoration: none;
`;

const unselectedLinkCss = css`
  color: ${COLORS.PALETTE.GREYSCALE.DARKER};
`;

const Link = styled(NavLink)<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  ${({ selected }) => (selected ? selectedLinkCss : unselectedLinkCss)}

  &:hover,
  &:active,
  &:focus {
    ${({ selected }) => (selected ? selectedLinkCss : unselectedLinkCss)}
  }
`;

const NavListItem = styled.li<{ selected?: boolean; isSMScreenOrBigger: boolean; isMDScreenOrBigger: boolean }>`
  position: relative;
  display: flex;
  min-width: 60px;
  justify-content: center;

  &:not(:last-child) {
    margin-right: ${SPACING.STATIC.LG.rem};
    ${({ isSMScreenOrBigger }) => !isSMScreenOrBigger && `margin-right: ${SPACING.STATIC.SM.rem};`}
  }

  > a {
    color: ${({ selected }) => (selected ? COLORS.TEXT_COLORS.PRIMARY : COLORS.TEXT_COLORS.DISABLED)};
    &:hover,
    &:active,
    &:focus {
      color: ${({ selected }) => (selected ? COLORS.TEXT_COLORS.PRIMARY : COLORS.TEXT_COLORS.DISABLED)};
    }
  }

  ${({ isMDScreenOrBigger }) => !isMDScreenOrBigger && `min-width: 20px;`}
`;

const NavBadge = styled.div<{ isSMScreenOrBigger: boolean }>`
  position: absolute;
  top: -4px;
  right: 14px;
  font-size: 8px;
  justify-content: center;
  align-items: center;
  height: 14px;
  min-width: 14px;
  line-height: 10px;
  border-radius: 14px;
  border: 2px solid ${COLORS.BACKGROUND_COLORS.WHITE};
  text-align: center;
  background: ${COLORS.BACKGROUND_COLORS.DANGER};
  color: ${COLORS.TEXT_COLORS.WHITE};
  ${({ isSMScreenOrBigger }) => !isSMScreenOrBigger && `top: -4px; right: 0px;`}
`;

const NotificationsNavBadge = styled(NavBadge)<{ isSMScreenOrBigger: boolean }>`
  right: 28px;
  ${({ isSMScreenOrBigger }) => !isSMScreenOrBigger && `top: -4px; right: 0px;`}
`;

type Props = {
  readonly authorized: boolean;
  readonly closeBanner?: () => void;
  readonly onboarding: boolean;
  readonly showBanner: boolean;
  readonly showProgyny: boolean;
};

export const AuthorizedDashboardNavbar = ({
  authorized,
  closeBanner,
  onboarding,
  showBanner,
  showProgyny,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: Props) => {
  const location = useLocation();
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const { isSMScreen, isSMScreenOrBigger, isMDScreenOrBigger } = useScreenSize();

  const { cartSize } = useCartData();
  const isCartShowing = useSelectorShared(getIsCartShowing);
  const badgeCounts = useSelectorShared(getBadgeCounts);
  const supportCasesWithUnreadMessages = useSelectorShared(getSupportCasesWithUnreadMessages);

  const routeName = location.pathname;
  const isActiveTab = (isCurrentRoute: (route: string) => boolean) => (routeName: string) => {
    return isCurrentRoute(routeName) && !isCartShowing;
  };
  const isAccountIconActive = isActiveTab(isAccountRoute)(routeName);
  const isMedsIconActive = isActiveTab(isMedsRoute)(routeName);
  const isOrderIconActive = isActiveTab(isOrdersRoute)(routeName);
  const isHelpIconActive = isActiveTab(isSupportRoute)(routeName);
  const isHomeIconActive = isActiveTab(isHomeRoute)(routeName);
  const isNotificationsIconActive = isActiveTab(isNotificationsRoute)(routeName);
  const currentRoute = routeFromPath(routeName);
  const brandLink = authorized ? '/home' : '/';
  const showProgynyBackground = showProgyny && onboarding;
  const isOnboardingVerify = currentRoute === VERIFY;
  const isOnboarding = onboarding && !isOnboardingVerify;
  const inCheckout = useMemo(() => routeName.match(/checkout/g), [routeName]);

  // relationship is set in redux when adding a family member, hide nav bar items when in that flow
  const relationship = useSelectorShared(getAddFamilyMemberRelationship);
  const isAddingFamilyMember = isAddFamilyMemberRoute(routeName, relationship);

  const { patientTasks = 0 } = badgeCounts || {};
  const { unreadNotificationsCount } = useFetchAllNotifications();
  const showNotificationsDot = unreadNotificationsCount > 0;
  const { supportCases } = useGetSupportCases();
  const unreadSupportCases = supportCases.filter(
    ({ most_recent_message }) => most_recent_message?.admin_user_id && !most_recent_message.read_at,
  ).length;

  const showHelpDot = useMemo(() => {
    // supportCasesWithUnreadMessages tracks unread messages that are coming through the websocket while patient
    // is just on the help page whereas unreadSupportCases tracks unread messages that are called from the
    // support cases API. We only call the API when tabs are switched so if the patient stayed on the help page
    // and new messages are coming through, we wouldnt have a way to notify the patient without using the websocket
    return supportCasesWithUnreadMessages.size > 0 || unreadSupportCases > 0;
  }, [supportCasesWithUnreadMessages, unreadSupportCases]);

  const onNavItemClick = useCallback(
    (tabName: string) => {
      if (isCartShowing) {
        dispatch(closeCart());
      }
      trackEvent({
        event: EVENTS.TAB_BOTTOM_BAR_BUTTON_PRESSED,
        params: {
          tabName,
        },
      });
    },
    [dispatch, isCartShowing, trackEvent],
  );

  const onCartClick = () => {
    dispatch(openCart());
    onNavItemClick('Cart');
  };

  const handleKeyPressOpenCart = (e: { keyCode: number }) => {
    if (e.keyCode !== 13) return;
    dispatch(openCart());
    onNavItemClick('Cart');
  };

  useEffect(() => {
    if (inCheckout && showBanner) {
      closeBanner?.();
    }
  }, [inCheckout, closeBanner, showBanner]);

  if (onboarding) {
    return null;
  }

  return (
    <NavBarWrapper
      showProgynyBackground={showProgynyBackground}
      authorized={authorized}
      isOnboarding={isOnboarding}
    >
      <Row
        spaceBetween
        flexGrow={1}
      >
        <LinkLogo
          to={brandLink}
          authorized={authorized}
        />
        {isAddingFamilyMember || inCheckout ? null : (
          <NavItems>
            <NavListItem
              isSMScreenOrBigger={isSMScreenOrBigger}
              isMDScreenOrBigger={isMDScreenOrBigger}
              selected={isHomeIconActive}
            >
              <Link
                to="/home"
                selected={isHomeIconActive}
                aria-current={isHomeIconActive}
                tabIndex={0}
                onClick={() => {
                  onNavItemClick('Home');
                }}
              >
                <Icon
                  testID="home-icon"
                  name={isHomeIconActive ? 'home-duo' : 'home'}
                  accessibilityLabel="navigate to home"
                  type={setIconType(isHomeIconActive)}
                />
                {isSMScreen ? null : (
                  <Description
                    color={getColor(isHomeIconActive)}
                    fontFamily={isHomeIconActive ? 'semibold' : undefined}
                  >
                    Home
                  </Description>
                )}
                {patientTasks > 0 ? <NavBadge isSMScreenOrBigger={isSMScreenOrBigger}>{patientTasks}</NavBadge> : null}
              </Link>
            </NavListItem>
            <NavListItem
              isSMScreenOrBigger={isSMScreenOrBigger}
              isMDScreenOrBigger={isMDScreenOrBigger}
              selected={isMedsIconActive}
            >
              <Link
                to="/prescriptions"
                selected={isMedsIconActive}
                aria-current={isMedsIconActive}
                tabIndex={0}
                onClick={() => {
                  onNavItemClick('Medications');
                }}
              >
                <Icon
                  testID="meds-icon"
                  name={isMedsIconActive ? 'pillbottle-duo' : 'pillbottle'}
                  accessibilityLabel="navigate to medications"
                  type={setIconType(isMedsIconActive)}
                />
                {isSMScreen ? null : (
                  <Description
                    color={getColor(isMedsIconActive)}
                    fontFamily={isMedsIconActive ? 'semibold' : undefined}
                  >
                    Meds
                  </Description>
                )}
              </Link>
            </NavListItem>
            <NavListItem
              isSMScreenOrBigger={isSMScreenOrBigger}
              isMDScreenOrBigger={isMDScreenOrBigger}
              selected={isOrderIconActive}
            >
              <Link
                to="/orders"
                selected={isOrderIconActive}
                aria-current={isOrderIconActive}
                tabIndex={0}
                onClick={() => {
                  onNavItemClick('Orders');
                }}
              >
                <Icon
                  testID="deliveries-icon"
                  name={isOrderIconActive ? 'box-duo' : 'box'}
                  accessibilityLabel="navigate to orders"
                  type={setIconType(isOrderIconActive)}
                />
                {isSMScreen ? null : (
                  <Description
                    color={getColor(isOrderIconActive)}
                    fontFamily={isOrderIconActive ? 'semibold' : undefined}
                  >
                    Orders
                  </Description>
                )}
              </Link>
            </NavListItem>
            <NavListItem
              isSMScreenOrBigger={isSMScreenOrBigger}
              isMDScreenOrBigger={isMDScreenOrBigger}
              selected={isHelpIconActive}
            >
              <Link
                to={{
                  pathname: '/help',
                }}
                selected={isHelpIconActive}
                aria-current={isHelpIconActive}
                tabIndex={0}
                onClick={() => {
                  onNavItemClick('Help');
                }}
              >
                <Icon
                  testID="help-icon"
                  name={isHelpIconActive ? 'questionmark-duo' : 'questionmark'}
                  accessibilityLabel="navigate to help"
                  type={setIconType(isHelpIconActive)}
                />
                {isSMScreen ? null : (
                  <Description
                    color={getColor(isHelpIconActive)}
                    fontFamily={isHelpIconActive ? 'semibold' : undefined}
                  >
                    Support
                  </Description>
                )}
                {showHelpDot ? (
                  <NavBadge isSMScreenOrBigger={isSMScreenOrBigger}>
                    {unreadSupportCases + supportCasesWithUnreadMessages.size}
                  </NavBadge>
                ) : null}
              </Link>
            </NavListItem>
            <NavListItem
              isSMScreenOrBigger={isSMScreenOrBigger}
              isMDScreenOrBigger={isMDScreenOrBigger}
              selected={isAccountIconActive}
            >
              <Link
                to="/me"
                selected={isAccountIconActive}
                aria-current={isAccountIconActive}
                tabIndex={0}
                onClick={() => {
                  onNavItemClick('Account');
                }}
              >
                <Icon
                  testID="account-icon"
                  name={isAccountIconActive ? 'usercircle-duo' : 'usercircle'}
                  accessibilityLabel="navigate to account"
                  type={setIconType(isAccountIconActive)}
                />
                {isSMScreen ? null : (
                  <Description
                    color={getColor(isAccountIconActive)}
                    fontFamily={isAccountIconActive ? 'semibold' : undefined}
                  >
                    Account
                  </Description>
                )}
              </Link>
            </NavListItem>
            <NavListItem
              isMDScreenOrBigger={isMDScreenOrBigger}
              isSMScreenOrBigger={isSMScreenOrBigger}
              selected={isCartShowing}
            >
              <Link
                to={location.pathname}
                onClick={onCartClick}
                onKeyDown={handleKeyPressOpenCart}
                tabIndex={0}
              >
                <Icon
                  testID="cart-icon"
                  name={isCartShowing ? 'cart-duo' : 'cart'}
                  accessibilityLabel="open cart"
                  type={setIconType(isCartShowing)}
                />
                {isSMScreen ? null : (
                  <Description
                    fontFamily={isCartShowing ? 'semibold' : undefined}
                    color={getColor(isCartShowing)}
                  >
                    Cart
                  </Description>
                )}
                {cartSize > 0 ? <NavBadge isSMScreenOrBigger={isSMScreenOrBigger}>{cartSize}</NavBadge> : null}
              </Link>
              {isCartShowing ? <Cart /> : null}
            </NavListItem>
            <NavListItem
              isSMScreenOrBigger={isSMScreenOrBigger}
              isMDScreenOrBigger={isMDScreenOrBigger}
              selected={isNotificationsIconActive}
            >
              <Link
                to="/notifications"
                state={{ unreadNotificationsCount }}
                selected={isNotificationsIconActive}
                aria-current={isNotificationsIconActive}
                tabIndex={0}
                onClick={() => {
                  onNavItemClick('Notifications');
                }}
              >
                <Icon
                  testID="notifications-icon"
                  name={isNotificationsIconActive ? 'bell-duo' : 'bell'}
                  accessibilityLabel="navigate to notifications"
                  type={setIconType(isNotificationsIconActive)}
                />
                {showNotificationsDot ? <NotificationsNavBadge isSMScreenOrBigger={isSMScreenOrBigger} /> : null}
                {isSMScreen ? null : (
                  <Description
                    color={getColor(isNotificationsIconActive)}
                    fontFamily={isNotificationsIconActive ? 'semibold' : undefined}
                  >
                    Notifications
                  </Description>
                )}
              </Link>
            </NavListItem>
          </NavItems>
        )}
      </Row>
    </NavBarWrapper>
  );
};
